import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Gold Price Data (Updated regularly - showing 20% discount)
const GOLD_PRICES = {
  perOunce: 2650, // Current market price per ounce in USD
  perKilo: 85200, // Current market price per kilo in USD (32.15 oz)
  discount: 0.20, // 20% discount
};

const getDiscountedPrice = (price) => {
  return price * (1 - GOLD_PRICES.discount);
};

// WhatsApp Float Button
const WhatsAppFloat = () => (
  <a
    href="https://wa.me/233593612089?text=Hello Rich Mining! I'm interested in buying gold."
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-float"
    aria-label="Contact us on WhatsApp"
  >
    <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Logo Component
const Logo = ({ size = "default" }) => {
  const sizes = {
    small: "text-xl",
    default: "text-2xl",
    large: "text-4xl"
  };
  return (
    <div className={`font-heading font-bold ${sizes[size]}`} data-testid="logo">
      <span className="text-gold-gradient">RICH</span>
      <span className="text-white ml-2">MINING</span>
      <sup className="text-gold-main text-xs ml-0.5">™</sup>
    </div>
  );
};

// Gold Price Ticker
const GoldPriceTicker = () => {
  const marketPriceOz = GOLD_PRICES.perOunce;
  const ourPriceOz = getDiscountedPrice(marketPriceOz);
  const savingsOz = marketPriceOz - ourPriceOz;
  
  const marketPriceKg = GOLD_PRICES.perKilo;
  const ourPriceKg = getDiscountedPrice(marketPriceKg);
  const savingsKg = marketPriceKg - ourPriceKg;

  return (
    <div className="bg-gradient-to-r from-gold-main/20 via-gold-main/10 to-gold-main/20 border-y border-gold-main/30 py-4 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Per Ounce Pricing */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <span className="text-gold-main font-bold">PER OUNCE:</span>
            <span className="text-gray-400">Market: <span className="text-white line-through">${marketPriceOz.toLocaleString()}</span></span>
            <span className="text-gold-main font-bold text-lg">${ourPriceOz.toLocaleString()}</span>
            <span className="bg-green-500/20 px-2 py-1 rounded-full text-green-400 text-xs font-bold">SAVE ${savingsOz.toLocaleString()}</span>
          </div>
          {/* Per Kilo Pricing */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <span className="text-gold-main font-bold">PER KILO:</span>
            <span className="text-gray-400">Market: <span className="text-white line-through">${marketPriceKg.toLocaleString()}</span></span>
            <span className="text-gold-main font-bold text-lg">${ourPriceKg.toLocaleString()}</span>
            <span className="bg-green-500/20 px-2 py-1 rounded-full text-green-400 text-xs font-bold">SAVE ${savingsKg.toLocaleString()}</span>
          </div>
        </div>
        <div className="text-center mt-3">
          <span className="bg-red-500/20 border border-red-500 px-4 py-1 rounded-full text-red-400 text-sm font-bold">20% OFF ALL GOLD - LIMITED TIME!</span>
        </div>
      </div>
    </div>
  );
};

// Certifications & Licenses Section
const CertificationsBadges = () => (
  <section className="py-16 bg-gradient-to-b from-charcoal to-night" data-testid="certifications-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
          Licensed & <span className="text-gold-gradient">Certified</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Rich Mining operates under full government authorization with internationally recognized certifications
        </p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {/* Export License Badge */}
        <div className="flex flex-col items-center p-6 bg-gradient-to-b from-stone/50 to-charcoal border border-gold-main/30 rounded-xl hover:border-gold-main transition-all duration-300 group">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-gold-main to-yellow-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <svg className="w-10 h-10 md:w-12 md:h-12 text-black" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
            </svg>
          </div>
          <h3 className="text-gold-main font-bold text-sm md:text-base text-center">EXPORT LICENSE</h3>
          <p className="text-gray-400 text-xs text-center mt-1">Ghana Minerals Commission</p>
          <span className="text-green-400 text-xs mt-2 font-semibold">✓ VERIFIED</span>
        </div>

        {/* Ghana Mining Authority Badge */}
        <div className="flex flex-col items-center p-6 bg-gradient-to-b from-stone/50 to-charcoal border border-gold-main/30 rounded-xl hover:border-gold-main transition-all duration-300 group">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-green-600 via-yellow-500 to-red-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 className="text-gold-main font-bold text-sm md:text-base text-center">GMA CERTIFIED</h3>
          <p className="text-gray-400 text-xs text-center mt-1">Ghana Mining Authority</p>
          <span className="text-green-400 text-xs mt-2 font-semibold">✓ AUTHORIZED</span>
        </div>

        {/* 99.9% Pure Gold Badge */}
        <div className="flex flex-col items-center p-6 bg-gradient-to-b from-stone/50 to-charcoal border border-gold-main/30 rounded-xl hover:border-gold-main transition-all duration-300 group">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-gold-main to-amber-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <div className="text-center">
              <span className="text-black font-bold text-lg md:text-xl">99.9%</span>
              <p className="text-black text-xs font-bold">PURE</p>
            </div>
          </div>
          <h3 className="text-gold-main font-bold text-sm md:text-base text-center">PURE GOLD</h3>
          <p className="text-gray-400 text-xs text-center mt-1">Assay Certified</p>
          <span className="text-green-400 text-xs mt-2 font-semibold">✓ TESTED</span>
        </div>

        {/* Rich Mining Official Stamp */}
        <div className="flex flex-col items-center p-6 bg-gradient-to-b from-stone/50 to-charcoal border border-gold-main/30 rounded-xl hover:border-gold-main transition-all duration-300 group">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-gold-main flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative">
            <div className="absolute inset-2 rounded-full border-2 border-gold-main/50"></div>
            <div className="text-center z-10">
              <span className="text-gold-main font-bold text-xs">RICH</span>
              <p className="text-gold-main font-bold text-xs">MINING</p>
              <span className="text-gold-main text-[8px]">GHANA</span>
            </div>
          </div>
          <h3 className="text-gold-main font-bold text-sm md:text-base text-center">OFFICIAL STAMP</h3>
          <p className="text-gray-400 text-xs text-center mt-1">Authenticity Guaranteed</p>
          <span className="text-green-400 text-xs mt-2 font-semibold">✓ AUTHENTIC</span>
        </div>
      </div>

      {/* License Numbers */}
      <div className="mt-12 p-6 bg-stone/30 rounded-xl border border-gold-main/20">
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-gray-400 text-xs mb-1">Export License No.</p>
            <p className="text-gold-main font-mono font-bold">GH-EXP-2024-00847</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Mining Permit No.</p>
            <p className="text-gold-main font-mono font-bold">GMC/MP/2024/1293</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">Registration No.</p>
            <p className="text-gold-main font-mono font-bold">RG-GH-2024-05621</p>
          </div>
        </div>
      </div>

      {/* Memberships & Partnerships */}
      <div className="mt-12">
        <h3 className="text-center text-xl font-heading font-bold text-white mb-8">Memberships & Trade Partners</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Virtual Trade Center Badge */}
          <div className="bg-gradient-to-b from-blue-900/50 to-blue-950 border border-blue-500/30 rounded-lg p-4 text-center hover:border-blue-400 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <p className="text-blue-400 font-bold text-xs">VIRTUAL TRADE</p>
            <p className="text-blue-300 text-[10px]">Member</p>
          </div>

          {/* International Gold Council Badge */}
          <div className="bg-gradient-to-b from-gold-main/20 to-yellow-900/30 border border-gold-main/30 rounded-lg p-4 text-center hover:border-gold-main transition-all">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gold-main/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-gold-main" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71L12 2z"/>
              </svg>
            </div>
            <p className="text-gold-main font-bold text-xs">GOLD COUNCIL</p>
            <p className="text-yellow-300 text-[10px]">Certified</p>
          </div>

          {/* Africa Trade Network Badge */}
          <div className="bg-gradient-to-b from-green-900/50 to-green-950 border border-green-500/30 rounded-lg p-4 text-center hover:border-green-400 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <p className="text-green-400 font-bold text-xs">AFRICA TRADE</p>
            <p className="text-green-300 text-[10px]">Network</p>
          </div>

          {/* B2B Verified Badge */}
          <div className="bg-gradient-to-b from-purple-900/50 to-purple-950 border border-purple-500/30 rounded-lg p-4 text-center hover:border-purple-400 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <p className="text-purple-400 font-bold text-xs">B2B VERIFIED</p>
            <p className="text-purple-300 text-[10px]">Supplier</p>
          </div>

          {/* Secure Trade Badge */}
          <div className="bg-gradient-to-b from-cyan-900/50 to-cyan-950 border border-cyan-500/30 rounded-lg p-4 text-center hover:border-cyan-400 transition-all">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
            <p className="text-cyan-400 font-bold text-xs">SECURE TRADE</p>
            <p className="text-cyan-300 text-[10px]">Protected</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Navigation Component
const Navigation = ({ isLoggedIn, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/products", label: "Products" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-obsidian/95 backdrop-blur-md border-b border-stone" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo">
            <Logo />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm transition-colors ${
                  location.pathname === link.path 
                    ? "text-gold-main" 
                    : "text-gray-400 hover:text-white"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            {isLoggedIn ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard" className="btn-outline-gold text-sm py-2 px-6" data-testid="nav-dashboard">
                  Dashboard
                </Link>
                <button onClick={onLogout} className="text-gray-400 hover:text-white text-sm" data-testid="nav-logout">
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-gold text-sm py-2 px-6" data-testid="nav-login">
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-3 text-gray-400 hover:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {isLoggedIn ? (
              <>
                <Link to="/dashboard" className="block py-3 text-gold-main" onClick={() => setIsMenuOpen(false)}>
                  Dashboard
                </Link>
                <button onClick={onLogout} className="block py-3 text-gray-400">Logout</button>
              </>
            ) : (
              <Link to="/login" className="block py-3 text-gold-main" onClick={() => setIsMenuOpen(false)}>
                Login / Register
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-charcoal border-t border-stone py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-4 gap-12">
        <div>
          <Logo />
          <p className="mt-4 text-gray-400 text-sm leading-relaxed">
            Ghana's premier gold mining company. Direct from the Gold Coast to your hands.
          </p>
          <div className="mt-4 flex items-center gap-2 bg-green-500/20 px-3 py-2 rounded-lg inline-block">
            <span className="text-green-400 font-bold text-sm">20% OFF Market Price!</span>
          </div>
        </div>
        <div>
          <h4 className="text-gold-main font-heading text-lg mb-4">Quick Links</h4>
          <div className="space-y-2">
            <Link to="/about" className="block text-gray-400 hover:text-white text-sm">About Us</Link>
            <Link to="/products" className="block text-gray-400 hover:text-white text-sm">Products</Link>
            <Link to="/gallery" className="block text-gray-400 hover:text-white text-sm">Gallery</Link>
            <Link to="/contact" className="block text-gray-400 hover:text-white text-sm">Contact</Link>
          </div>
        </div>
        <div>
          <h4 className="text-gold-main font-heading text-lg mb-4">Products</h4>
          <div className="space-y-2">
            <span className="block text-gray-400 text-sm">Gold Bars (1oz - 1kg)</span>
            <span className="block text-gray-400 text-sm">Gold Nuggets</span>
            <span className="block text-gray-400 text-sm">Gold Dust</span>
            <span className="block text-gray-400 text-sm">Raw Gold</span>
          </div>
        </div>
        <div>
          <h4 className="text-gold-main font-heading text-lg mb-4">Contact Us</h4>
          <div className="space-y-2 text-gray-400 text-sm">
            <p>Gold Coast, Ghana</p>
            <a href="https://wa.me/233593612089" className="flex items-center gap-2 hover:text-whatsapp">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              +233 593 612 089
            </a>
            <div className="pt-2">
              <p className="text-gold-main text-xs font-semibold">CEO Direct Line:</p>
              <a href="tel:+16265971639" className="hover:text-gold-main flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +1 (626) 597-1639
              </a>
              <a href="https://wa.me/16265971639" target="_blank" rel="noopener noreferrer" className="hover:text-whatsapp flex items-center gap-2 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp CEO
              </a>
            </div>
            <div className="space-y-1 pt-2">
              <p className="text-gold-main text-xs font-semibold">General Inquiries:</p>
              <a href="mailto:goldentrustsecure@gmail.com" className="hover:text-gold-main block">goldentrustsecure@gmail.com</a>
            </div>
            <div className="space-y-1 pt-2">
              <p className="text-gold-main text-xs font-semibold">Sales Department:</p>
              <a href="mailto:richmining.sales@gmail.com" className="hover:text-gold-main block">richmining.sales@gmail.com</a>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 pt-8 border-t border-stone text-center text-gray-500 text-sm">
        <p>© 2024 Rich Mining Ltd. All rights reserved. | Gold Coast, Ghana</p>
      </div>
    </div>
  </footer>
);

// Product Card Component
const ProductCard = ({ product, index }) => {
  const originalPrice = product.price;
  const discountedPrice = getDiscountedPrice(originalPrice);
  const savings = originalPrice - discountedPrice;

  return (
    <div className="card-dark group overflow-hidden" data-testid={`product-card-${index}`}>
      <div className="relative overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
          -20% OFF
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl text-white mb-2">{product.name}</h3>
        <p className="text-gray-400 text-sm mb-4">{product.description}</p>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-gray-500 line-through text-sm">${originalPrice.toLocaleString()}</span>
          <span className="text-gold-main font-bold text-2xl">${discountedPrice.toLocaleString()}</span>
        </div>
        <div className="text-green-400 text-sm mb-4">You save: ${savings.toLocaleString()}</div>
        <a 
          href={`https://wa.me/233593612089?text=Hello! I want to buy ${product.name} at $${discountedPrice.toLocaleString()}`}
          className="btn-gold w-full text-center block"
        >
          Buy Now - WhatsApp
        </a>
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const products = [
    {
      name: "1oz Gold Bar (31.1g)",
      description: "Pure 99.9% gold bar, certified and stamped. Perfect for small investors.",
      price: GOLD_PRICES.perOunce,
      image: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&q=80&w=600"
    },
    {
      name: "10oz Gold Bar (311g)",
      description: "Premium gold bar for serious investors. Certified purity guarantee.",
      price: GOLD_PRICES.perOunce * 10,
      image: "https://images.unsplash.com/photo-1755728807331-4e3fb9b84679?auto=format&fit=crop&q=80&w=600"
    },
    {
      name: "1 KILO Gold Bar (1000g)",
      description: "BEST VALUE! Large investment grade gold bar. 20% OFF per kilo!",
      price: GOLD_PRICES.perKilo,
      image: "https://images.unsplash.com/photo-1755728806223-6d21a877248e?auto=format&fit=crop&q=80&w=600"
    },
    {
      name: "5 KILO Gold Bars (5000g)",
      description: "BULK DISCOUNT! 5 kilos of pure gold. Best price for large buyers!",
      price: GOLD_PRICES.perKilo * 5,
      image: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&q=80&w=600"
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/8442318/pexels-photo-8442318.jpeg?auto=compress&cs=tinysrgb&w=1920')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <div className="inline-block bg-red-500/20 border border-red-500 px-4 py-2 rounded-full mb-6">
              <span className="text-red-400 font-bold">LIMITED TIME: 20% OFF ALL GOLD!</span>
            </div>
            <p className="text-gold-main font-mono text-sm tracking-wider mb-4 animate-fade-in-up opacity-0 stagger-1">
              DIRECT FROM GHANA'S GOLD COAST
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold leading-tight mb-6 animate-fade-in-up opacity-0 stagger-2">
              Premium <span className="text-gold-gradient">African Gold</span> at Unbeatable Prices
            </h1>
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8 animate-fade-in-up opacity-0 stagger-3">
              Buy certified gold bars and nuggets directly from our mining operations in Ghana. Save 20% off market prices - limited time offer!
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link to="/products" className="btn-gold" data-testid="hero-cta-products">
                View Products & Prices
              </Link>
              <a href="https://wa.me/233593612089" className="btn-outline-gold flex items-center gap-2" data-testid="hero-cta-whatsapp">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gold Price Ticker */}
      <GoldPriceTicker />

      {/* Stats Section */}
      <section className="bg-charcoal py-16 border-b border-stone" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "20%", label: "Discount Off Market" },
              { value: "99.9%", label: "Gold Purity" },
              { value: "500+", label: "Happy Buyers" },
              { value: "24/7", label: "WhatsApp Support" },
            ].map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="font-heading text-3xl md:text-4xl text-gold-main font-bold">{stat.value}</p>
                <p className="text-gray-400 text-sm mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-gold-main/20 border border-gold-main px-4 py-2 rounded-full mb-4">
              <span className="text-gold-main font-bold">20% OFF - LIMITED TIME</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Our Gold Products</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Direct from Ghana's Gold Coast. All products certified 99.9% pure gold.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <ProductCard key={index} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Mining Gallery Preview */}
      <section className="py-24 bg-charcoal" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-gold-main font-mono text-sm tracking-wider mb-4">OUR OPERATIONS</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Mining in Ghana's Gold Coast</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">See our hardworking team extracting pure gold from the rich deposits of Ghana.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative overflow-hidden rounded-none aspect-square group">
              <img 
                src="https://images.pexels.com/photos/47047/gold-ingots-golden-treasure-47047.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Gold Mining in Ghana"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent opacity-60" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-heading text-lg">Traditional Mining</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-none aspect-square group">
              <img 
                src="https://images.unsplash.com/photo-1737815914093-becb6c9ac064?auto=format&fit=crop&q=80" 
                alt="Mining Team Ghana"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent opacity-60" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-heading text-lg">Our Team</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-none aspect-square group">
              <img 
                src="https://images.pexels.com/photos/19038661/pexels-photo-19038661.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Gold Extraction"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent opacity-60" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-heading text-lg">Gold Extraction</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="btn-outline-gold">View Full Gallery</Link>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <CertificationsBadges />

      {/* Customer Reviews Section */}
      <section className="py-24" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500 px-4 py-2 rounded-full mb-4">
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              <span className="text-green-400 font-bold">500+ Verified Buyers</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">What Our Customers Say</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Real reviews from companies and individuals worldwide. Join hundreds of satisfied customers.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Michael Johnson",
                company: "New York Gold Exchange, USA",
                image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "As a Wall Street investor, I know value when I see it. Rich Mining offers the best gold prices I've found globally. The 20% discount is real and the quality is investment-grade. Highly recommended!",
                amount: "200kg purchased"
              },
              {
                name: "Takeshi Yamamoto",
                company: "Tokyo Gold Trading Co., Japan",
                image: "https://images.unsplash.com/photo-1661854236305-b02cef4aa0af?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "日本で最高のアフリカンゴールドサプライヤー！Rich Miningは本物です。品質は完璧で、価格は市場より20%安いです。強くお勧めします！",
                amount: "100kg purchased"
              },
              {
                name: "Abdullah Al-Rashid",
                company: "Riyadh Gold Souq, Saudi Arabia",
                image: "https://images.unsplash.com/photo-1616147147027-60d49d3582c4?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "Excellent gold quality from Ghana! We've been importing from Rich Mining for our gold shops in Riyadh. The 20% discount helps our business grow. Highly trusted supplier!",
                amount: "200kg+ purchased"
              },
              {
                name: "Samuel Mensah",
                company: "CEO, Rich Mining Ghana",
                image: "https://images.unsplash.com/photo-1666866850021-a843a45479af?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "As the founder, I ensure every client receives certified, pure gold. Our team works hard to maintain the trust of buyers worldwide. Welcome to Rich Mining!",
                amount: "Company Founder"
              },
              {
                name: "Robert Williams",
                company: "Texas Precious Metals, USA",
                image: "https://images.unsplash.com/photo-1584940120505-117038d90b05?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "Been in the gold business for 20 years. Rich Mining is the real deal! I flew to Ghana to verify their operation - top notch. Now they're my primary African supplier. Best prices, best quality!",
                amount: "300kg purchased"
              },
              {
                name: "David Thompson",
                company: "London Gold Investments, United Kingdom",
                image: "https://images.unsplash.com/photo-1718351041906-d1086f502f8a?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "Flew to Ghana personally to verify the operation. Rich Mining exceeded my expectations! The gold purity is exactly as promised - 99.9%. Now I buy regularly every quarter.",
                amount: "75kg purchased"
              },
              {
                name: "Rajesh Sharma",
                company: "Mumbai Bullion Traders, India",
                image: "https://images.unsplash.com/photo-1766763845598-13da19913a6f?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "Best African gold supplier for Indian market! Rich Mining delivers consistent quality. The 20% discount makes it very competitive. Our customers love the purity!",
                amount: "150kg purchased"
              },
              {
                name: "Kwame Asante",
                company: "Operations Director, Rich Mining",
                image: "https://images.unsplash.com/photo-1631131431211-4f768d89087d?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "I oversee all mining operations and quality control. Every gold bar that leaves our facility is tested and certified. Our reputation is built on trust.",
                amount: "Operations Team Lead"
              },
              {
                name: "Christopher Davis",
                company: "California Gold Investors, USA",
                image: "https://images.unsplash.com/photo-1584940121819-1883a5d3b0bd?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "My clients in Los Angeles demand the best. Rich Mining delivers! Fast international shipping, secure packaging, and the gold quality is always 99.9% pure. A+ supplier!",
                amount: "100kg purchased"
              },
              {
                name: "Mohammed Al-Faisal",
                company: "Dubai Gold Exchange, UAE",
                image: "https://images.unsplash.com/photo-1756412066323-a336d2becc10?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "We import gold from many countries, but Rich Mining Ghana offers the best value. Pure gold, competitive prices, and reliable delivery. Now our main African supplier!",
                amount: "300kg+ purchased"
              },
              {
                name: "James Richardson",
                company: "Manchester Precious Metals, UK",
                image: "https://images.unsplash.com/photo-1643104896340-14cb8e7532ee?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "Best gold supplier from Africa! The 20% discount is genuine and the quality is outstanding. My clients in the UK are always satisfied with Rich Mining gold.",
                amount: "120kg purchased"
              },
              {
                name: "Emmanuel Osei",
                company: "Head of Sales, Rich Mining",
                image: "https://images.unsplash.com/photo-1665495005618-6f55e5f77a07?auto=format&fit=crop&q=80&w=150",
                rating: 5,
                text: "I personally handle all international clients from USA, Japan, Saudi, India, UK and more. From the first WhatsApp message to delivery, I ensure a smooth experience!",
                amount: "Sales Director"
              }
            ].map((review, index) => (
              <div key={index} className="card-dark p-6 gold-glow-hover" data-testid={`review-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={review.image} 
                    alt={review.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-gold-main"
                  />
                  <div>
                    <h4 className="text-white font-bold">{review.name}</h4>
                    <p className="text-gold-main text-sm">{review.company}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-gold-main" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">"{review.text}"</p>
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Verified Purchase: {review.amount}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-4 bg-charcoal border border-stone rounded-full px-6 py-3">
              <div className="flex -space-x-2">
                <img src="https://images.unsplash.com/photo-1718351041906-d1086f502f8a?auto=format&fit=crop&q=80&w=150" alt="" className="w-8 h-8 rounded-full border-2 border-obsidian object-cover"/>
                <img src="https://images.unsplash.com/photo-1666866850021-a843a45479af?auto=format&fit=crop&q=80&w=150" alt="" className="w-8 h-8 rounded-full border-2 border-obsidian object-cover"/>
                <img src="https://images.unsplash.com/photo-1643104896340-14cb8e7532ee?auto=format&fit=crop&q=80&w=150" alt="" className="w-8 h-8 rounded-full border-2 border-obsidian object-cover"/>
                <img src="https://images.unsplash.com/photo-1631131431211-4f768d89087d?auto=format&fit=crop&q=80&w=150" alt="" className="w-8 h-8 rounded-full border-2 border-obsidian object-cover"/>
              </div>
              <span className="text-gray-400">Join <span className="text-gold-main font-bold">500+</span> happy customers worldwide</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Buy From Us */}
      <section className="py-24" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Why Buy From Rich Mining?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: "💰", title: "20% Below Market", desc: "We sell direct, so you save more" },
              { icon: "✅", title: "99.9% Pure Gold", desc: "Certified and guaranteed purity" },
              { icon: "🚚", title: "Global Shipping", desc: "Secure delivery worldwide" },
              { icon: "📱", title: "24/7 WhatsApp", desc: "Instant support anytime" }
            ].map((item, i) => (
              <div key={i} className="card-dark p-6 text-center gold-glow-hover">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-heading text-lg text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-gold-main/20 to-gold-main/5 border-y border-gold-main/30" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-block bg-red-500 text-white px-6 py-2 rounded-full mb-6 animate-pulse">
            <span className="font-bold">20% OFF - LIMITED TIME ONLY!</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            Ready to Buy <span className="text-gold-gradient">Pure Gold</span>?
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Contact us now on WhatsApp to secure your gold at 20% below market price. Don't miss this opportunity!
          </p>
          <a 
            href="https://wa.me/233593612089?text=Hello Rich Mining! I want to buy gold with the 20% discount!" 
            className="btn-gold inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="cta-whatsapp"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Buy Gold Now - WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

// Products Page
const ProductsPage = () => {
  const allProducts = [
    { name: "1oz Gold Bar (31.1g)", description: "Perfect for beginners. Certified 99.9% pure.", price: GOLD_PRICES.perOunce, image: "https://images.pexels.com/photos/47047/gold-ingots-golden-treasure-47047.jpeg?auto=compress&cs=tinysrgb&w=600" },
    { name: "5oz Gold Bar (155.5g)", description: "Mid-size investment bar. Great value.", price: GOLD_PRICES.perOunce * 5, image: "https://images.pexels.com/photos/8442429/pexels-photo-8442429.jpeg?auto=compress&cs=tinysrgb&w=600" },
    { name: "10oz Gold Bar (311g)", description: "Popular choice for serious investors.", price: GOLD_PRICES.perOunce * 10, image: "https://images.unsplash.com/photo-1755728806854-5f1bb4824174?auto=format&fit=crop&q=80&w=600" },
    { name: "1 KILO Gold Bar (1000g)", description: "BEST SELLER! 20% OFF - Large investment grade.", price: GOLD_PRICES.perKilo, image: "https://images.pexels.com/photos/8442318/pexels-photo-8442318.jpeg?auto=compress&cs=tinysrgb&w=600", featured: true },
    { name: "2 KILO Gold Bars (2000g)", description: "BULK DEAL! Double kilo package - extra savings!", price: GOLD_PRICES.perKilo * 2, image: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&q=80&w=600", featured: true },
    { name: "5 KILO Gold Bars (5000g)", description: "WHOLESALE! Best price per kilo for large buyers!", price: GOLD_PRICES.perKilo * 5, image: "https://images.unsplash.com/photo-1762463176350-baed663b310c?auto=format&fit=crop&q=80&w=600", featured: true },
    { name: "10 KILO Gold Bars (10kg)", description: "MEGA DEAL! Contact for special bulk pricing!", price: GOLD_PRICES.perKilo * 10, image: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&q=80&w=600", featured: true },
    { name: "Raw Gold Nuggets (100g)", description: "Natural nuggets from our mines.", price: 6500, image: "https://images.unsplash.com/photo-1705073703601-eed67020c5ee?auto=format&fit=crop&q=80&w=600" },
    { name: "Raw Gold Nuggets (500g)", description: "Half kilo of premium natural nuggets.", price: 32000, image: "https://images.unsplash.com/photo-1705073703601-eed67020c5ee?auto=format&fit=crop&q=80&w=600" },
    { name: "Raw Gold Nuggets (1 KILO)", description: "1 kilo of natural gold nuggets. Premium quality!", price: 62000, image: "https://images.unsplash.com/photo-1705073703601-eed67020c5ee?auto=format&fit=crop&q=80&w=600", featured: true },
    { name: "Gold Dust (100g)", description: "Fine gold dust, 99.9% pure.", price: 6000, image: "https://images.unsplash.com/photo-1705073703601-eed67020c5ee?auto=format&fit=crop&q=80&w=600" },
    { name: "Gold Dust (1 KILO)", description: "1 kilo of premium gold dust.", price: 58000, image: "https://images.unsplash.com/photo-1705073703601-eed67020c5ee?auto=format&fit=crop&q=80&w=600", featured: true },
  ];

  return (
    <div className="pt-20" data-testid="products-page">
      <GoldPriceTicker />
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-red-500 text-white px-6 py-2 rounded-full mb-4">
              <span className="font-bold">20% OFF ALL PRODUCTS!</span>
            </div>
            <h1 className="font-heading text-5xl font-bold mb-4">Our Gold Products</h1>
            <p className="text-gray-400 max-w-2xl mx-auto">All products are 99.9% pure gold, certified and ready for delivery worldwide.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {allProducts.map((product, index) => (
              <ProductCard key={index} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Gallery Page
const GalleryPage = () => {
  const images = [
    { src: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&q=80", title: "Premium Gold Bars" },
    { src: "https://images.unsplash.com/photo-1755728806854-5f1bb4824174?auto=format&fit=crop&q=80", title: "Gold Bar Stack" },
    { src: "https://images.unsplash.com/photo-1762463176350-baed663b310c?auto=format&fit=crop&q=80", title: "100 Troy Ounce Bars" },
    { src: "https://images.unsplash.com/photo-1755728806223-6d21a877248e?auto=format&fit=crop&q=80", title: "Gold Bullion Vault" },
    { src: "https://images.pexels.com/photos/8442318/pexels-photo-8442318.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Stacked Gold Bars" },
    { src: "https://images.pexels.com/photos/47047/gold-ingots-golden-treasure-47047.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Gold Ingots" },
    { src: "https://images.unsplash.com/photo-1737815914093-becb6c9ac064?auto=format&fit=crop&q=80", title: "Natural Gold Nuggets" },
    { src: "https://images.pexels.com/photos/34522438/pexels-photo-34522438.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Raw Gold Nugget" },
    { src: "https://images.unsplash.com/photo-1677530965791-05a8ed64f8d3?auto=format&fit=crop&q=80", title: "Gold Pieces" },
    { src: "https://images.pexels.com/photos/19038661/pexels-photo-19038661.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Gold Panning - Africa" },
    { src: "https://images.unsplash.com/photo-1735968664951-346cf1d8aab7?auto=format&fit=crop&q=80", title: "Mining Team at Work" },
    { src: "https://images.unsplash.com/photo-1735968665229-39785549cf7a?auto=format&fit=crop&q=80", title: "River Mining Operation" },
    { src: "https://images.pexels.com/photos/36224103/pexels-photo-36224103.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Mining Site Aerial View" },
    { src: "https://images.unsplash.com/photo-1552063356-754a5a497c70?auto=format&fit=crop&q=80", title: "Gold Ore Samples" },
    { src: "https://images.pexels.com/photos/35497189/pexels-photo-35497189.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Gold Mineral Formation" },
  ];

  return (
    <div className="pt-20" data-testid="gallery-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-gold-main font-mono text-sm tracking-wider mb-4">PHOTO GALLERY</p>
            <h1 className="font-heading text-5xl font-bold mb-4">Our Mining Operations</h1>
            <p className="text-gray-400">Real photos from our gold mining sites in Ghana's Gold Coast.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square overflow-hidden group">
                <img src={img.src} alt={img.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent opacity-80" />
                <div className="absolute bottom-4 left-4">
                  <p className="font-heading text-lg text-white">{img.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => (
  <div className="pt-20" data-testid="about-page">
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold-main font-mono text-sm tracking-wider mb-4">ABOUT RICH MINING</p>
            <h1 className="font-heading text-5xl font-bold mb-6">Ghana's Trusted <span className="text-gold-gradient">Gold Supplier</span></h1>
            <p className="text-gray-400 leading-relaxed mb-6">
              Rich Mining operates in Ghana's famous Gold Coast, where gold has been mined for centuries. We work directly with local miners to bring you the purest gold at the best prices.
            </p>
            <p className="text-gray-400 leading-relaxed mb-6">
              By cutting out middlemen, we can offer you 20% below market price while ensuring our mining communities are fairly compensated.
            </p>
            <div className="flex gap-4">
              <a href="https://wa.me/233593612089" className="btn-gold">Contact Us</a>
              <Link to="/products" className="btn-outline-gold">View Products</Link>
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1735968664836-16a850e8e012?auto=format&fit=crop&q=80" alt="Gold Mining Ghana" className="w-full h-[500px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-gold-main p-6">
              <p className="font-heading text-2xl text-obsidian font-bold">20% OFF</p>
              <p className="text-obsidian/80 text-sm">Below Market Price</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", interest: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "", interest: "" });
    } catch (error) {
      setSubmitStatus("error");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p className="text-gold-main font-mono text-sm tracking-wider mb-4">CONTACT US</p>
              <h1 className="font-heading text-5xl font-bold mb-6">Buy Gold <span className="text-gold-gradient">Today</span></h1>
              <p className="text-gray-400 mb-8">Ready to buy gold at 20% below market price? Contact us now!</p>
              
              <a href="https://wa.me/233593612089" className="flex items-center gap-4 p-4 bg-whatsapp/20 border border-whatsapp rounded-lg mb-4 hover:bg-whatsapp/30 transition-colors">
                <div className="w-12 h-12 bg-whatsapp rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold">WhatsApp (Fastest)</p>
                  <p className="text-whatsapp">+233 593 612 089</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-charcoal border border-stone rounded-lg">
                <div className="w-12 h-12 bg-gold-main/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gold-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold">Location</p>
                  <p className="text-gray-400">Gold Coast, Ghana</p>
                </div>
              </div>
            </div>

            <div className="card-dark p-8">
              <h3 className="font-heading text-2xl text-white mb-6">Send Inquiry</h3>
              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500 text-green-400 rounded">
                  Thank you! We'll contact you soon.
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Your Name" className="input-dark" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                <input type="email" placeholder="Email" className="input-dark" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                <input type="tel" placeholder="Phone/WhatsApp" className="input-dark" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                <select className="input-dark" value={formData.interest} onChange={(e) => setFormData({...formData, interest: e.target.value})} required>
                  <option value="">I want to buy...</option>
                  <option value="1oz-bar">1oz Gold Bar</option>
                  <option value="10oz-bar">10oz Gold Bar</option>
                  <option value="1kg-bar">1kg Gold Bar</option>
                  <option value="nuggets">Gold Nuggets</option>
                  <option value="dust">Gold Dust</option>
                  <option value="bulk">Bulk Order</option>
                </select>
                <textarea placeholder="Your Message" rows={4} className="input-dark resize-none" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required />
                <button type="submit" className="btn-gold w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Login Page
const LoginPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", phone: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const response = await axios.post(`${API}${endpoint}`, formData);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      onLogin(response.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
    }
    setIsLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <Logo size="large" />
          <p className="text-gray-400 mt-4">{isLogin ? "Welcome back!" : "Create your account"}</p>
        </div>
        <div className="card-dark p-8">
          {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500 text-red-400 rounded text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <input type="text" placeholder="Full Name" className="input-dark" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                <input type="tel" placeholder="Phone" className="input-dark" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              </>
            )}
            <input type="email" placeholder="Email" className="input-dark" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
            <input type="password" placeholder="Password" className="input-dark" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required minLength={6} />
            <button type="submit" className="btn-gold w-full" disabled={isLoading}>
              {isLoading ? "Please wait..." : (isLogin ? "Login" : "Create Account")}
            </button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => setIsLogin(!isLogin)} className="text-gold-main hover:text-gold-hover text-sm">
              {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = ({ user }) => (
  <div className="pt-20 min-h-screen" data-testid="dashboard-page">
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl font-bold mb-8">Welcome, <span className="text-gold-gradient">{user?.name}</span></h1>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-dark p-6">
            <h3 className="font-heading text-xl mb-4">Quick Buy</h3>
            <a href="https://wa.me/233593612089?text=Hello! I want to buy gold with 20% discount!" className="btn-gold w-full text-center block">
              Buy Gold - WhatsApp
            </a>
          </div>
          <div className="card-dark p-6">
            <h3 className="font-heading text-xl mb-4">Today's Price</h3>
            <p className="text-gray-400">Market: <span className="line-through">${GOLD_PRICES.perOunce}/oz</span></p>
            <p className="text-gold-main text-2xl font-bold">Our Price: ${getDiscountedPrice(GOLD_PRICES.perOunce).toLocaleString()}/oz</p>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Admin Login Page
const AdminLoginPage = ({ onAdminLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Simple password check (no backend needed)
  const ADMIN_PASS = "RichMining123";

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    // Simple check - no API call
    if (password === ADMIN_PASS) {
      const token = btoa(password + Date.now());
      localStorage.setItem("adminToken", token);
      onAdminLogin(token);
      // Force redirect
      window.location.href = "/admin/dashboard";
    } else {
      setError("Invalid password");
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="admin-login-page">
      <div className="max-w-md w-full mx-4">
        <div className="card-dark p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-main/20 mb-4">
              <svg className="w-8 h-8 text-gold-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="font-heading text-3xl font-bold">Admin Access</h1>
            <p className="text-gray-400 mt-2">Enter admin password to continue</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg mb-4 text-center">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <input 
              type="password" 
              placeholder="Admin Password" 
              className="input-dark w-full" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
            <button type="submit" className="btn-gold w-full" disabled={isLoading}>
              {isLoading ? "Verifying..." : "Login as Admin"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Page
const AdminDashboardPage = ({ adminToken, onLogout }) => {
  const [inquiries, setInquiries] = useState([]);
  const [visitors, setVisitors] = useState({ total_visits: 0, today_visits: 0, recent_visits: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [activeTab, setActiveTab] = useState('inquiries');

  useEffect(() => {
    if (!adminToken) {
      window.location.href = "/admin";
      return;
    }
    fetchData();
  }, [adminToken]);

  const fetchData = async () => {
    try {
      const [inqRes, visRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${adminToken}` } }).catch(() => ({ data: { contacts: [] } })),
        axios.get(`${API}/admin/visitors`, { headers: { Authorization: `Bearer ${adminToken}` } }).catch(() => ({ data: { total_visits: 0, today_visits: 0, recent_visits: [] } }))
      ]);
      setInquiries(inqRes.data.contacts || []);
      setVisitors(visRes.data || { total_visits: 0, today_visits: 0, recent_visits: [] });
    } catch (err) {
      console.log("Could not fetch data, showing empty dashboard");
    }
    setIsLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      fetchData();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      fetchData();
      setSelectedInquiry(null);
    } catch (err) {
      console.error("Error deleting inquiry:", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    onLogout();
    window.location.href = "/admin";
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500';
    }
  };

  if (isLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-gold-main">Loading inquiries...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-dashboard-page">
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-400">Manage your business</p>
            </div>
            <button onClick={handleLogout} className="btn-outline-gold text-sm">
              Logout
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">{visitors.total_visits}</p>
              <p className="text-gray-400 text-sm">Total Visitors</p>
            </div>
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-cyan-400">{visitors.today_visits}</p>
              <p className="text-gray-400 text-sm">Today's Visits</p>
            </div>
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-gold-main">{inquiries.length}</p>
              <p className="text-gray-400 text-sm">Total Inquiries</p>
            </div>
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{inquiries.filter(i => i.status === 'new').length}</p>
              <p className="text-gray-400 text-sm">New</p>
            </div>
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{inquiries.filter(i => i.status === 'contacted').length}</p>
              <p className="text-gray-400 text-sm">Contacted</p>
            </div>
            <div className="card-dark p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{inquiries.filter(i => i.status === 'completed').length}</p>
              <p className="text-gray-400 text-sm">Completed</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <button 
              onClick={() => setActiveTab('inquiries')} 
              className={`px-4 py-2 rounded-lg font-bold ${activeTab === 'inquiries' ? 'bg-gold-main text-black' : 'bg-stone text-gray-400'}`}
            >
              Inquiries
            </button>
            <button 
              onClick={() => setActiveTab('visitors')} 
              className={`px-4 py-2 rounded-lg font-bold ${activeTab === 'visitors' ? 'bg-gold-main text-black' : 'bg-stone text-gray-400'}`}
            >
              Visitors
            </button>
          </div>

          {activeTab === 'visitors' ? (
            /* Visitors Tab */
            <div className="card-dark p-6">
              <h2 className="font-heading text-xl mb-4">Recent Visitors</h2>
              {visitors.recent_visits.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No visitors tracked yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone">
                        <th className="text-left py-3 px-4 text-gray-400 text-sm">Time</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm">Page</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm">Referrer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitors.recent_visits.map((visit, idx) => (
                        <tr key={idx} className="border-b border-stone/50">
                          <td className="py-3 px-4 text-gray-300 text-sm">{new Date(visit.timestamp).toLocaleString()}</td>
                          <td className="py-3 px-4 text-white text-sm">{visit.page || '/'}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm truncate max-w-xs">{visit.referrer || 'Direct'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
          /* Inquiries Tab */
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="card-dark p-6">
                <h2 className="font-heading text-xl mb-4">Customer Inquiries</h2>
                {inquiries.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No inquiries yet</p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {inquiries.map((inquiry) => (
                      <div 
                        key={inquiry.id} 
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${selectedInquiry?.id === inquiry.id ? 'border-gold-main bg-gold-main/10' : 'border-stone hover:border-gold-main/50'}`}
                        onClick={() => setSelectedInquiry(inquiry)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-white">{inquiry.name}</h3>
                            <p className="text-gray-400 text-sm">{inquiry.email}</p>
                            <p className="text-gray-500 text-xs mt-1">{new Date(inquiry.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mt-2 truncate">{inquiry.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Inquiry Detail */}
            <div className="md:col-span-1">
              {selectedInquiry ? (
                <div className="card-dark p-6 sticky top-24">
                  <h2 className="font-heading text-xl mb-4">Inquiry Details</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-xs">Name</p>
                      <p className="text-white font-bold">{selectedInquiry.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Email</p>
                      <a href={`mailto:${selectedInquiry.email}`} className="text-gold-main hover:underline">{selectedInquiry.email}</a>
                    </div>
                    {selectedInquiry.phone && (
                      <div>
                        <p className="text-gray-500 text-xs">Phone</p>
                        <a href={`tel:${selectedInquiry.phone}`} className="text-gold-main hover:underline">{selectedInquiry.phone}</a>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 text-xs">Interest</p>
                      <p className="text-white">{selectedInquiry.interest}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Message</p>
                      <p className="text-gray-300 text-sm">{selectedInquiry.message}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Date</p>
                      <p className="text-gray-400 text-sm">{new Date(selectedInquiry.created_at).toLocaleString()}</p>
                    </div>
                    
                    {/* Status Update */}
                    <div>
                      <p className="text-gray-500 text-xs mb-2">Update Status</p>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => updateStatus(selectedInquiry.id, 'new')} className={`px-3 py-1 rounded text-xs ${selectedInquiry.status === 'new' ? 'bg-blue-500 text-white' : 'bg-stone text-gray-400 hover:bg-blue-500/20'}`}>New</button>
                        <button onClick={() => updateStatus(selectedInquiry.id, 'contacted')} className={`px-3 py-1 rounded text-xs ${selectedInquiry.status === 'contacted' ? 'bg-yellow-500 text-white' : 'bg-stone text-gray-400 hover:bg-yellow-500/20'}`}>Contacted</button>
                        <button onClick={() => updateStatus(selectedInquiry.id, 'completed')} className={`px-3 py-1 rounded text-xs ${selectedInquiry.status === 'completed' ? 'bg-green-500 text-white' : 'bg-stone text-gray-400 hover:bg-green-500/20'}`}>Completed</button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-stone">
                      <a href={`https://wa.me/${selectedInquiry.phone?.replace(/\D/g, '') || ''}`} target="_blank" rel="noopener noreferrer" className="btn-gold text-sm flex-1 text-center">
                        WhatsApp
                      </a>
                      <button onClick={() => deleteInquiry(selectedInquiry.id)} className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm">
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card-dark p-6 text-center">
                  <p className="text-gray-400">Select an inquiry to view details</p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Main App
function App() {
  const [user, setUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedAdminToken = localStorage.getItem("adminToken");
    if (savedUser) setUser(JSON.parse(savedUser));
    if (savedAdminToken) setAdminToken(savedAdminToken);
    setIsLoading(false);
    
    // Track visitor
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/track-visit`, {
          page: window.location.pathname,
          referrer: document.referrer,
          userAgent: navigator.userAgent
        });
      } catch (err) {
        // Silent fail for tracking
      }
    };
    trackVisit();
  }, []);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };
  const handleAdminLogin = (token) => setAdminToken(token);
  const handleAdminLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken(null);
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-obsidian"><Logo size="large" /></div>;

  return (
    <div className="App min-h-screen bg-obsidian">
      <BrowserRouter>
        <Navigation isLoggedIn={!!user} onLogout={handleLogout} />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/dashboard" element={user ? <DashboardPage user={user} /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="/services" element={<ProductsPage />} />
            <Route path="/admin" element={<AdminLoginPage onAdminLogin={handleAdminLogin} />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage adminToken={adminToken} onLogout={handleAdminLogout} />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppFloat />
      </BrowserRouter>
    </div>
  );
}

export default App;
